<template>
  <div class="quote__list-insured">
    <div class="quote__accordion">
      <BaseAccordion alwaysDisplay>
        <template v-slot:name>
          {{ $t('renew_car_policy_info_contacts_header') }}
        </template>

        <template v-slot:content>
          <div class="inputList contact-form user-contact-info">
            <BaseInputPhone
              :error="$v.phoneInfo_.phone.$dirty && $v.phoneInfo_.phone.$invalid"
              :valid="!$v.phoneInfo_.phone.$invalid"
              inputMode="mini"
              :default-country-code="phoneInfo_?.countryCode"
              v-model="phoneInfo_.phone"
              @phone-number-blur="trackPhoneInput"
              @update="onUpdatePhone" />

            <BaseInput
              :placeholder="$t('qic_user_contacts_email')"
              :validation="$v.email_"
              name="email"
              v-model="email_"
              @blur="onEmailBlur" />

            <DatePicker
              v-if="!hideDOB"
              class="missing-info__dob"
              :showAlways="true"
              :value="date"
              @input="onInput"
              :max-date="maxDate"
              :min-date="minDate"
              :placeholder="$t('renew.common.dob')"
            />

            <div class="error-input-message" v-if="isError && !hideDOB">
              {{$t('qic.error.dob_qid_mismatch')}}
            </div>

          </div>
        </template>
      </BaseAccordion>
    </div>
  </div>
</template>

<script>
import { required, email } from 'vuelidate/lib/validators'
import { BaseInput, BaseAccordion, BaseInputPhone, DatePicker } from 'qic-components'
import { calculateAge, trackGtag } from '@/utils/useGtag'
import { mapState } from 'vuex'

export default {
  name: 'UserContactsForm',
  components: {
    BaseInputPhone,
    BaseInput,
    BaseAccordion,
    DatePicker
  },
  props: {
    email: { type: String },
    phone: { type: String },
    dob: { type: [String, Date] }
  },
  emits: [
    'onSubmit'
  ],
  data () {
    return {
      phoneInfo_: {
        phone: this.phone,
        countryCode: 'QA'
      },
      email_: this.email,
      date: this.dob,
      isError: false,
      /** https://qicbizdev.atlassian.net/browse/QIC-2615 Turn off upgrade flow (for investigation) */
      hideDOB: true
    }
  },
  computed: {
    ...mapState({
      policyInfo: state => state.renew.policyInfo,
      userData: state => state.renew.userData
    }),
    maxDate () {
      const now = new Date()
      return new Date(now.getFullYear() - 18, now.getMonth(), now.getDate())
    },
    minDate () {
      const now = new Date()
      return new Date(now.getFullYear() - 70, now.getMonth(), now.getDate())
    },
    isFormInvalid () {
      return this.$v.$invalid
    },
    isCompanyId () {
      return this.userData.qid.length === 8
    }
  },
  methods: {
    parseDate (dateStr) {
      const datePatternDMY = /^\d{2}\/\d{2}\/\d{4}$/

      if (datePatternDMY.test(dateStr)) {
        const parts = dateStr.split('/')
        const day = parseInt(parts[0], 10)
        const month = parseInt(parts[1], 10) - 1
        const year = parseInt(parts[2], 10)

        const date = new Date(year, month, day)
        if (date.getFullYear() === year && date.getMonth() === month && date.getDate() === day) {
          return date
        } else {
          return dateStr
        }
      }

      const date = new Date(dateStr)

      if (!isNaN(date.getTime())) {
        return date
      }

      return dateStr
    },
    getInitialDOB () {
      if (
        this.$store.state.motor.dateOfBirth &&
        this.validateBirthDateByQID(new Date(this.$store.state.motor.dateOfBirth))
      ) {
        return new Date(this.$store.state.motor.dateOfBirth)
      }
      const parsedDob = this.parseDate(this.dob)
      if (this.dob && this.validateBirthDateByQID(parsedDob)) {
        return parsedDob
      }

      return this.startDate()
    },
    trackPhoneInput () {
      if (this.phoneInfo_.phone) {
        trackGtag('typePhone', { label: this.phoneInfo_.phone.length.toString() })
      }
    },
    trackError (errorText, position) {
      trackGtag('input_error', {
        label: errorText,
        position
      })
    },
    onSubmit () {
      this.$v.$touch()
      this.email_ = this.email_.toLowerCase()
      if (this.submitDisabled()) return
      this.$emit('onSubmit', {
        email: this.email_,
        phoneInfo: this.phoneInfo_
      })
    },
    onEmailBlur (event) {
      if (this.email_) {
        trackGtag('typeEmail', { label: this.email_.length.toString() })
      }
      if (event && this.email_ !== event.target.value) {
        this.email_ = event.target.value
      }
      this.email_ = this.email_.toLowerCase()
      this.$store.commit('setUserEmail', this.email_)
    },
    onUpdatePhone (result) {
      this.phoneInfo_.formattedPhone = result?.formattedNumber()
      this.phoneInfo_.isValidLength = result?.isValidLength()
      this.phoneInfo_.countryCode = result?.countryCode
      this.$v.phoneInfo_.$reset()
    },
    validateAndGetValues () {
      this.$v.$touch()

      if (!this.hideDOB) {
        const dob = this.validateAndGetValuesDOB()

        if (!dob) {
          trackGtag('input_error', {
            category: 'carIns',
            page: 'policy_info',
            input_field: 'Date of birth',
            policy_id: this.policyInfo.policyNo,
            text: 'QID number doesn\'t match the date of birth. Check the information and try again'
          })
          return
        }
      }

      if (this.$v.email_.$error) {
        this.trackError('Fill the field', 'Email field')
      }

      if (this.$v.phoneInfo_.phone.$error) {
        this.trackError('Fill the field', 'Phone field')
      }

      if (this.$v.$invalid) return false

      return {
        phone: this.phoneInfo_.phone,
        email: this.email_
        // dob
      }
    },

    submitDisabled () {
      return this.$v.$invalid
    },

    startDate () {
      const DEFAULT_BIRTHDAY_DATE = new Date(1990, 0, 1)

      const qid = this.userData.qid
      if (qid && qid.length > 2) {
        const centuryIndicator = qid.charAt(0)
        const yearInCentury = parseInt(qid.substring(1, 3))

        let fullYear
        if (centuryIndicator === '2') {
          fullYear = 1900 + yearInCentury
        } else if (centuryIndicator === '3') {
          fullYear = 2000 + yearInCentury
        } else {
          return DEFAULT_BIRTHDAY_DATE
        }

        return new Date(fullYear, 0, 1)
      }
      return DEFAULT_BIRTHDAY_DATE
    },
    onInput (date) {
      this.date = date
      this.isError = false
    },
    validateBirthDateByQID (inputDate = this.date) {
      const qid = this.userData.qid

      if (this.isCompanyId) {
        return true
      }

      if (!inputDate || !qid || qid.length < 3) {
        return true
      }

      const centuryDigit = parseInt(qid.charAt(0))
      const yearOfBirth = parseInt(qid.substring(1, 3))

      const century = (centuryDigit === 2) ? 1900 : (centuryDigit === 3) ? 2000 : 0
      if (!century) {
        return false
      }

      const fullYearOfBirth = century + yearOfBirth

      return inputDate.getFullYear() === fullYearOfBirth
    },
    validateAndGetValuesDOB () {
      if (this.isFormInvalid) return
      this.isError = !this.validateBirthDateByQID()
      if (this.isError) {
        return
      }

      const dateOfBirth = new Date(
        this.date.getFullYear(),
        this.date.getMonth(),
        this.date.getDate()
      )

      trackGtag('selectBirthDate', {
        label: this.date.toLocaleDateString('ru-RU', {
          day: 'numeric',
          month: 'numeric',
          year: 'numeric'
        }),
        age: calculateAge(dateOfBirth)
      })

      return dateOfBirth
    }
  },
  mounted () {
    if (!this.email) {
      this.email_ = this.userData.email
    }
    this.date = this.getInitialDOB()
  },
  validations () {
    const validations = {}
    validations.email_ = {
      required,
      email
    }
    validations.phoneInfo_ = {
      phone: {
        validPhone: () => this.phoneInfo_.isValidLength || false
      }
    }
    return validations
  }
}
</script>

<style lang="scss">
.error-input-message {
  color: #F94C27;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;

  margin: 0 24px;

  @media (max-width: 375px) {
    margin: 0;
  }
}

.missing-info__dob {
  .placeholder-container {
    z-index: 999;

    .mainInp:focus {
      border: 1px solid rgba(43, 39, 255, 0.8) !important;
    }
  }
}

.dobPicker_calendar {
  position: inherit;
}

.dobPicker_calendar .vc-highlight {
  background-color: #5F27FF !important;
  width: 32px !important;
  height: 32px !important;
  color: inherit !important;
}

.rtl .user-contact-info {
  .mainInp {
    text-align: right;
    padding: 30px 30px 15px 60px;
  }

  .placeholder-container label {
    right: 20px !important;
  }

  .acceptInp {
    background-position: left 20px center !important;
  }

  .clearInput {
    left: 20px !important;
    right: inherit !important;
  }

  .maskInp .flex-1 {
   width: auto;
  }

  .input-phone-number {
    background-position: right 20px center !important;
  }

  .input-tel__input {
    width: 140px;
    text-align: left;
  }

  .vue-phone-number-input {
    display: flex;
    justify-content: end;
  }
}
</style>
